import { Controller } from "@hotwired/stimulus";

const HEX = /^#[0-9a-f]{6}$/i;

// Keeps the snippet text and the preview in sync with the two color fields.
// Each color has a swatch and a hex box that mirror each other, so a school can
// either pick visually or paste a brand hex.
//
// Colors are never persisted — they only ever live in the snippet text, which is
// why changing them here cannot affect a snippet already installed on a website.
export default class extends Controller {
  static targets = [
    "primarySwatch", "primaryHex",
    "secondarySwatch", "secondaryHex",
    "snippet", "previewFill"
  ];

  static values = {
    template: String,
    defaultPrimary: String,
    defaultSecondary: String
  };

  connect() {
    this.render();
  }

  // The native color well always reports a valid #rrggbb, so mirror it straight
  // into the hex box.
  swatchChanged({ currentTarget }) {
    const hex = currentTarget === this.primarySwatchTarget ? this.primaryHexTarget : this.secondaryHexTarget;

    hex.value = currentTarget.value;
    hex.classList.remove("is-invalid");
    this.render();
  }

  // Typed hex only reaches the swatch and the preview once it is complete and
  // valid, so a half-typed value cannot repaint everything mid-keystroke.
  hexChanged({ currentTarget }) {
    const swatch = currentTarget === this.primaryHexTarget ? this.primarySwatchTarget : this.secondarySwatchTarget;
    const value = currentTarget.value.trim();
    const valid = HEX.test(value);

    currentTarget.classList.toggle("is-invalid", !valid);
    if (!valid) return;

    swatch.value = value;
    this.render();
  }

  resetPrimary(event) {
    event.preventDefault();
    this.set(this.primarySwatchTarget, this.primaryHexTarget, this.defaultPrimaryValue);
  }

  resetSecondary(event) {
    event.preventDefault();
    this.set(this.secondarySwatchTarget, this.secondaryHexTarget, this.defaultSecondaryValue);
  }

  set(swatch, hex, value) {
    swatch.value = value;
    hex.value = value;
    hex.classList.remove("is-invalid");
    this.render();
  }

  render() {
    const fill = this.primarySwatchTarget.value;
    const text = this.secondarySwatchTarget.value;

    this.snippetTarget.textContent = this.templateValue
      .replace(/data-primary-color="[^"]*"/, `data-primary-color="${fill}"`)
      .replace(/data-secondary-color="[^"]*"/, `data-secondary-color="${text}"`);

    // Every filled element in the live widget — the active tab, the selected
    // amount and the submit button — takes the highlight as its background with
    // the other color as its text. Mirroring that here rather than inventing a
    // layout is what makes the preview trustworthy.
    this.previewFillTargets.forEach((element) => {
      element.style.backgroundColor = fill;
      element.style.borderColor = fill;
      element.style.color = text;
    });
  }
}
